import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "@descope/react-sdk";
import { useLanguage } from "@nxt-shared/i18n";
import { buildTenantGroups } from "./organizationGroups.js";
import { isLocalhost, tickarooPlatformDomain } from "../utils/tickarooPlatformDomain.js";
import { sessionAccess, sessionSubject } from "../utils/sessionSubject.js";
export { buildTenantGroups } from "./organizationGroups.js";
// Long enough that moving between pages mounts every caller from the cache; a later refetch runs in
// the background and keeps the status at "success".
const LISTING_STALE_TIME = 5 * 60 * 1000;
/** Gatekeeper is served from the Tickaroo `www.<domain>` host; its wildcard CORS permits this. */
function gatekeeperMeUrl(domain) {
    return `https://www.${domain}/api/gatekeeper/v1/me/organizations`;
}
/**
 * The management "my organizations" listing on the Tickaroo apps apex. Same-origin on the apex
 * (identical to what /me does today); a CORS-allowed cross-origin call from a tenant subdomain; relative on localhost so the dev gateway proxy handles it (preserving local /me).
 */
function managementMeUrl(domain) {
    if (isLocalhost(window.location.hostname))
        return "/management/api/me/organizations";
    return `https://apps.${domain}/management/api/me/organizations`;
}
async function fetchJson(url, token, signal) {
    const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        signal,
    });
    if (!response.ok)
        throw new Error(`Request failed (HTTP ${response.status})`);
    return (await response.json());
}
// The key names the user and what their token grants, not the token: a refresh that only rotates the
// token keeps the listing, one that changes access (an entered tenant, a new role) reloads it.
// SessionQueryClientProvider also swaps the cache when the user changes.
function useListing(source, url, fallbackMessage) {
    const { sessionToken } = useSession();
    const subject = sessionSubject(sessionToken);
    const domain = tickarooPlatformDomain();
    const query = useQuery({
        queryKey: ["member-organizations", source, subject, sessionAccess(sessionToken)],
        queryFn: ({ signal }) => fetchJson(url(domain), sessionToken, signal),
        enabled: subject !== undefined && domain !== null,
        retry: false,
        staleTime: LISTING_STALE_TIME,
    });
    // `refetch` ignores `enabled`; without this guard it would send the token to a non-platform host.
    const refetch = () => {
        if (subject !== undefined && domain !== null)
            void query.refetch();
    };
    // Signed out: nothing cached may render, even before the provider has swapped the cache.
    if (subject === undefined)
        return { status: "loading", error: null, refetch, data: undefined };
    if (domain === null)
        return { status: "unavailable", error: null, refetch, data: undefined };
    // A failed background refetch keeps the listing it already has.
    if (query.data !== undefined)
        return { status: "success", error: null, refetch, data: query.data };
    // A retry after a failure shows as loading, as the per-source retry affordances expect.
    if (query.isError && !query.isFetching) {
        const error = query.error instanceof Error ? query.error : new Error(fallbackMessage);
        return { status: "error", error, refetch, data: undefined };
    }
    return { status: "loading", error: null, refetch, data: undefined };
}
/**
 * Loads the authenticated caller's tenant groups (liveblog + nxt-cms) for the topbar switcher, the
 * /me launchpad and the /me sidebar. Each source loads independently, so a failure or slow load of
 * one never blanks the other; `groups` is rebuilt from whatever has loaded.
 */
export function useMemberOrganizationGroups() {
    const language = useLanguage();
    const { data: lbData, ...liveblog } = useListing("liveblog", gatekeeperMeUrl, "Failed to load Liveblog organizations");
    const { data: nxtData, ...nxt } = useListing("nxt", managementMeUrl, "Failed to load NXT organizations");
    const groups = useMemo(() => buildTenantGroups(lbData?.tenants ?? [], nxtData?.organizations ?? [], language), [lbData, nxtData, language]);
    return { groups, liveblog, nxt };
}
/**
 * The caller is known to be a member of no liveblog organization: the liveblog listing has loaded
 * and holds none. False while loading, after a failure and where the listing is unavailable.
 */
export function hasNoLiveblogOrganizations({ groups, liveblog }) {
    return liveblog.status === "success" && groups.every((group) => group.liveblogOrgs.length === 0);
}
//# sourceMappingURL=useMemberOrganizationGroups.js.map